/**
 * Item Review Task Component
 * 
 * Allows a participant to review and approve/reject items for inclusion
 * in the prioritization cycle.
 */

import { useState, useMemo } from 'react';
import {
  Save,
  CheckCircle,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
} from 'lucide-react';
import type {
  PrioritizationTask,
  ItemReviewResponse,
} from '../../../types/prioritization/prioritizationTask';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';

// ============================================================================
// Types
// ============================================================================

export interface ItemReviewTaskProps {
  task: PrioritizationTask;
  userId: string;
  userName: string;
  onComplete?: () => void;
}

type ReviewDecision = 'approved' | 'rejected' | 'needs_revision';

// ============================================================================
// Decision Button
// ============================================================================

interface DecisionButtonProps {
  decision: ReviewDecision;
  currentDecision?: ReviewDecision;
  onClick: () => void;
}

function DecisionButton({ decision, currentDecision, onClick }: DecisionButtonProps) {
  const isSelected = currentDecision === decision;
  
  const config: Record<ReviewDecision, { 
    icon: typeof CheckCircle; 
    label: string; 
    selectedBg: string; 
    selectedText: string;
  }> = {
    approved: {
      icon: ThumbsUp,
      label: 'Accept',
      selectedBg: 'bg-[var(--green-dim)] border-[var(--green)]',
      selectedText: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    },
    rejected: {
      icon: ThumbsDown,
      label: 'Reject',
      selectedBg: 'bg-[var(--coral-dim)] border-[var(--coral)]',
      selectedText: 'text-[var(--coral)]',
    },
    needs_revision: {
      icon: AlertTriangle,
      label: 'Needs Revision',
      selectedBg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
      selectedText: 'text-[var(--mustard)]',
    },
  };
  
  const { icon: Icon, label, selectedBg, selectedText } = config[decision];
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm  border-2 transition-all ${
        isSelected 
          ? `${selectedBg} ${selectedText} font-medium` 
          : 'border-[var(--rule)] hover:border-[var(--rule)] text-[var(--text-sec)]'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ItemReviewTask({ task, userId, userName, onComplete }: ItemReviewTaskProps) {
  // State
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [showNotes, setShowNotes] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get items from candidates store
  const allCandidates = useItemsStore(state => state.items);
  const items = useMemo(() => {
    return task.itemIds
      .map(id => allCandidates.find(c => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c);
  }, [task.itemIds, allCandidates]);
  
  // Store actions
  const submitResponse = usePrioritizationTaskStore(state => state.submitResponse);
  
  // Check completion
  const allItemsReviewed = items.every(item => decisions[item.id] !== undefined);
  const reviewedCount = items.filter(item => decisions[item.id] !== undefined).length;
  const progress = items.length > 0 ? (reviewedCount / items.length) * 100 : 0;
  
  // Count by decision
  const approvedCount = Object.values(decisions).filter(d => d === 'approved').length;
  const rejectedCount = Object.values(decisions).filter(d => d === 'rejected').length;
  const revisionCount = Object.values(decisions).filter(d => d === 'needs_revision').length;
  
  // Check if already submitted
  const existingResponse = task.responses.find(r => r.participantId === userId);
  
  const handleDecisionChange = (itemId: string, decision: ReviewDecision) => {
    setDecisions(prev => ({ ...prev, [itemId]: decision }));
  };
  
  const handleNoteChange = (itemId: string, note: string) => {
    setNotes(prev => ({ ...prev, [itemId]: note }));
  };
  
  const handleSubmit = async () => {
    if (!allItemsReviewed) return;
    
    setIsSubmitting(true);
    
    try {
      const responseData: ItemReviewResponse = {
        type: 'item-review',
        decisions,
        notes: Object.keys(notes).length > 0 ? notes : undefined,
      };
      
      submitResponse({
        taskId: task.id,
        participantId: userId,
        participantName: userName,
        data: responseData,
      });
      
      onComplete?.();
    } catch (error) {
      console.error('[ItemReviewTask] Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show existing response if already submitted
  if (existingResponse) {
    const data = existingResponse.data as ItemReviewResponse;
    const approved = Object.entries(data.decisions).filter(([_, d]) => d === 'approved').length;
    const rejected = Object.entries(data.decisions).filter(([_, d]) => d === 'rejected').length;
    const revision = Object.entries(data.decisions).filter(([_, d]) => d === 'needs_revision').length;
    
    return (
      <div className="space-y-6">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
          <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Review submitted</span>
          </div>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
            You reviewed {items.length} items on {new Date(existingResponse.submittedAt).toLocaleString()}.
          </p>
        </div>
        
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[var(--green-dim)] p-4 text-center">
            <ThumbsUp className="h-6 w-6 mx-auto text-[var(--green)] mb-2" />
            <p className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{approved}</p>
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Accepted</p>
          </div>
          <div className="bg-[var(--coral-dim)] p-4 text-center">
            <ThumbsDown className="h-6 w-6 mx-auto text-[var(--coral)] mb-2" />
            <p className="text-2xl font-bold text-[var(--coral)]">{rejected}</p>
            <p className="text-sm text-[var(--coral)]">Rejected</p>
          </div>
          <div className="bg-[var(--mustard-dim)] p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto text-[var(--mustard)] mb-2" />
            <p className="text-2xl font-bold text-[var(--mustard)]">{revision}</p>
            <p className="text-sm text-[var(--mustard)]">Needs Revision</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-cyan-500 mb-4" />
        <h2 className="text-xl font-semibold text-[var(--text-pri)]">
          Review Items
        </h2>
        <p className="text-[var(--text-sec)] mt-2">
          Review each item and decide whether it should be included in prioritization.
        </p>
      </div>
      
      {/* Progress */}
      <div className="bg-[var(--bg-raised)] border p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[var(--text-sec)]">Progress</span>
          <span className="font-medium text-[var(--text-pri)]">{reviewedCount} / {items.length} reviewed</span>
        </div>
        <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Decision Summary */}
        {reviewedCount > 0 && (
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
              <ThumbsUp className="h-3 w-3" /> {approvedCount}
            </span>
            <span className="flex items-center gap-1 text-[var(--coral)]">
              <ThumbsDown className="h-3 w-3" /> {rejectedCount}
            </span>
            <span className="flex items-center gap-1 text-[var(--mustard)]">
              <AlertTriangle className="h-3 w-3" /> {revisionCount}
            </span>
          </div>
        )}
      </div>
      
      {/* Items List */}
      <div className="bg-[var(--bg-raised)] border divide-y">
        {items.map((item, index) => (
          <div key={item.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-ter)]">#{index + 1}</span>
                  <h3 className="font-medium text-[var(--text-pri)]">{item.title}</h3>
                </div>
                {item.description && (
                  <p className="text-sm text-[var(--text-sec)] mt-1">{item.description}</p>
                )}
              </div>
            </div>
            
            {/* Decision Buttons */}
            <div className="flex items-center gap-2 mt-3">
              <DecisionButton
                decision="approved"
                currentDecision={decisions[item.id]}
                onClick={() => handleDecisionChange(item.id, 'approved')}
              />
              <DecisionButton
                decision="rejected"
                currentDecision={decisions[item.id]}
                onClick={() => handleDecisionChange(item.id, 'rejected')}
              />
              <DecisionButton
                decision="needs_revision"
                currentDecision={decisions[item.id]}
                onClick={() => handleDecisionChange(item.id, 'needs_revision')}
              />
            </div>
            
            {/* Notes */}
            <div className="mt-2">
              {showNotes[item.id] ? (
                <textarea
                  value={notes[item.id] || ''}
                  onChange={(e) => handleNoteChange(item.id, e.target.value)}
                  placeholder="Add feedback or notes..."
                  className="w-full px-3 py-2 text-sm border resize-none"
                  rows={2}
                />
              ) : (
                <button
                  onClick={() => setShowNotes(prev => ({ ...prev, [item.id]: true }))}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  <MessageSquare className="h-3 w-3" />
                  Add note
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Submit */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleSubmit}
          disabled={!allItemsReviewed || isSubmitting}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Save className="h-4 w-4" />
              Submit Review
            </>
          )}
        </button>
      </div>
      
      {!allItemsReviewed && (
        <div className="flex items-center gap-2 text-[var(--mustard)] text-sm">
          <AlertCircle className="h-4 w-4" />
          Please review all {items.length - reviewedCount} remaining items
        </div>
      )}
    </div>
  );
}

export default ItemReviewTask;
